/* eslint-disable no-restricted-globals */
import {
  FormEvent,
  FunctionComponent,
  useContext,
  useEffect,
  useState
} from "react";
import {
  IssuerConfigCreate,
  IssuerConfigCreations
} from "../utils/helpers/types";
import {
  createIssuerConfig,
  updateIssuerConfig
} from "../utils/helpers/data/issuer-config";
import styles from "./self-service.module.scss";
import Modal, { ModalProps } from "../components/modal/Modal";
import Button from "../components/button/Button";
import LayoutContext from "../context/layout-context";
import Input from "../components/input/Input";

const blankappService: IssuerConfigCreate = {
  name: "",
  issuerCode: "",
  apiVersion: 0,
  walletId: 0,
  additionalFields: ""
};

const AddEditModal: FunctionComponent<
  ModalProps & {
    issuerConfigOnEdit: IssuerConfigCreations | null;
    refresh: (stayOnPage: boolean) => void;
    cancel: () => void;
  }
> = ({ visible, cancel, issuerConfigOnEdit, refresh }) => {
  const [formData, setFormData] = useState<IssuerConfigCreate>({
    ...blankappService,
    ...issuerConfigOnEdit
  });

  const [isSaving, setIsSaving] = useState(false);
  const { notify } = useContext(LayoutContext);

  const handleChange = (key: keyof IssuerConfigCreate, value: unknown) => {
    setFormData(prevFormData => ({ ...prevFormData, [key]: value }));
  };

  useEffect(() => {
    setFormData(
      issuerConfigOnEdit
        ? { ...blankappService, ...issuerConfigOnEdit }
        : { ...blankappService }
    );
  }, [visible, issuerConfigOnEdit]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    setIsSaving(true);

    if (formData.name.length < 3) {
      notify("error", "Name must be at least 3 characters long");
      setIsSaving(false);
      return;
    }

    if (formData.issuerCode.length < 3) {
      notify("error", "Issuer code must be at least 3 characters long");
      setIsSaving(false);
      return;
    }
    if (formData.issuerCode.length > 10) {
      notify("error", "Issuer code must not exceed 10 characters long");
      setIsSaving(false);
      return;
    }
    if (isNaN(Number(formData.apiVersion))) {
      notify("error", "API Version must be a number");
      setIsSaving(false);
      return;
    }

    if (isNaN(Number(formData.walletId))) {
      notify("error", "Wallet ID must be a number");
      setIsSaving(false);
      return;
    }

    let id: number | undefined;

    if (issuerConfigOnEdit && typeof issuerConfigOnEdit.id === "number") {
      id = issuerConfigOnEdit.id;
    }

    if (id !== undefined) {
      const { error, message } = await updateIssuerConfig(id, {
        ...formData
      });

      if (error) {
        notify("error", `Unable to update issuer config: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully updated issuer config");
    } else {
      const { error, message } = await createIssuerConfig({
        ...formData
      });

      if (error) {
        notify("error", `Unable to create issuer config: ${message}`);
        setIsSaving(false);
        return;
      }

      notify("success", "Successfully created issuer config");
    }
    cancel?.();
    refresh(false);
    setIsSaving(false);
  };

  const isCreateOperation = issuerConfigOnEdit === null;

  return (
    <Modal cancel={cancel} visible={visible} size="small">
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <span className="question">Name</span>
          <Input
            value={formData.name}
            onChange={value => handleChange("name", value)}
            placeholder="Enter name"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Issuer Code</span>
          </div>
          <Input
            value={formData.issuerCode}
            onChange={value => handleChange("issuerCode", value)}
            placeholder="Enter issuer code"
            responsive
            required
            disabled={!isCreateOperation}
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">API Version</span>
          </div>
          <Input
            value={formData.apiVersion}
            onChange={value => handleChange("apiVersion", value)}
            placeholder="Enter apiVersion"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Wallet Id</span>
          </div>
          <Input
            value={formData.walletId}
            onChange={value => handleChange("walletId", value)}
            placeholder="Enter walletId"
            responsive
            required
          />
        </div>

        <div className="input-group">
          <div className={styles.span}>
            <span className="question">Additional Fields</span>
          </div>
          <Input
            value={formData.additionalFields}
            onChange={value => handleChange("additionalFields", value)}
            placeholder="Enter additionalFields"
            responsive
            required
          />
        </div>

        <br />
        <div className="flex spaced end vertical-margin">
          <Button type="accent" onClick={() => cancel?.()}>
            Cancel
          </Button>
          <Button buttonType="submit" loading={isSaving}>
            {issuerConfigOnEdit ? "Edit Issuer Config" : "Create Issuer Config"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
export default AddEditModal;
